import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  CreateQueueMessage,
  GetLayout,
  Location as GetLocation,
} from '../../libs/GraphQL';
import Loader from '../../components/atoms/Loader';
import SlotEditorModal from './SlotEditorModal';
import VendItemShelfLifeCache from './vendItemShelfLife';
import {
  FILL_MODES,
  FILL_MODE_DESCRIPTIONS,
  buildItemsBySlot,
  buildQueueMessageData,
  collapseSkuSlots,
  summarizeItemsBySlot,
} from './fillModeBuilders';
import { canSeedLocation, isProdCmsBuild } from './environment';
import { applyOverrides } from './slotOverrides';
import { readCache, writeCache } from './layoutCache';
import theme from './theme';
import {
  BackLink,
  Card,
  CardLabel,
  Eyebrow,
  FieldLabel,
  GhostButton,
  HeaderBar,
  Layout,
  ModalActions,
  ModalBackdrop,
  ModalBody,
  ModalCard,
  ModalTitle,
  ModeDescription,
  NumberInput,
  OverrideDot,
  Page,
  PrimaryButton,
  ProdBanner,
  ProdConfirmLabel,
  Slot,
  SlotCoord,
  SlotCount,
  SlotGrid,
  SlotName,
  SlotPill,
  SlotRow,
  SlotRowLabel,
  Subtitle,
  SummaryRow,
  TelemetryStrip,
  Title,
  ToggleLabel,
  ToggleRow,
  ToggleSwitch,
  reactSelectStyles,
} from './styles';

function effectiveSlots(stateOrSlots, onlyOneSlotPerSku) {
  const slots = Array.isArray(stateOrSlots) ? stateOrSlots : stateOrSlots.slots;
  const flag = Array.isArray(stateOrSlots)
    ? onlyOneSlotPerSku
    : stateOrSlots.onlyOneSlotPerSku;
  return flag ? collapseSkuSlots(slots) : slots;
}

function replanWithOverrides(state, nextOverrides, nextBaseline) {
  const baseline = nextBaseline !== undefined ? nextBaseline : state.baseline;
  if (!baseline) return null;
  return applyOverrides(
    baseline,
    nextOverrides,
    effectiveSlots(state),
    state.shelfLife,
  );
}

// --- state machine --------------------------------------------------------

const initial = {
  status: 'loading', // loading | ready | submitting | error
  layoutName: null,
  slots: [],
  shelfLife: {},
  mode: FILL_MODES.LAYOUT_DEFAULT,
  fillCount: '',
  shelfLifeDays: '',
  onlyOneSlotPerSku: false,
  slotOverrides: {}, // { "(x,y)": { count?, vendItemName?, daysUntilExpiration?, empty? } }
  editingSlot: null, // fmt string like "(0,0)" when the per-slot modal is open
  baseline: null, // pre-override itemsBySlot so overrides can re-apply in place
  plannedItemsBySlot: null,
  confirming: false,
  errorMessage: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'LAYOUT_LOADED':
      return {
        ...state,
        status: 'ready',
        layoutName: action.layoutName,
        slots: action.slots,
        shelfLife: action.shelfLife,
      };
    case 'LOAD_ERROR':
      return { ...state, status: 'error', errorMessage: action.message };
    case 'SET_MODE':
      return { ...state, mode: action.mode, plannedItemsBySlot: null };
    case 'SET_FILL_COUNT':
      return { ...state, fillCount: action.value, plannedItemsBySlot: null };
    case 'SET_SHELF_LIFE_DAYS':
      return {
        ...state,
        shelfLifeDays: action.value,
        plannedItemsBySlot: null,
      };
    case 'TOGGLE_COLLAPSE':
      return {
        ...state,
        onlyOneSlotPerSku: !state.onlyOneSlotPerSku,
        plannedItemsBySlot: null,
      };
    case 'OPEN_SLOT_EDITOR':
      return { ...state, editingSlot: action.slot };
    case 'CLOSE_SLOT_EDITOR':
      return { ...state, editingSlot: null };
    case 'SET_SLOT_OVERRIDE': {
      const next = { ...state.slotOverrides, [action.slot]: action.override };
      return {
        ...state,
        slotOverrides: next,
        editingSlot: null,
        plannedItemsBySlot: replanWithOverrides(state, next),
      };
    }
    case 'CLEAR_SLOT_OVERRIDE': {
      const next = { ...state.slotOverrides };
      delete next[action.slot];
      return {
        ...state,
        slotOverrides: next,
        editingSlot: null,
        plannedItemsBySlot: replanWithOverrides(state, next),
      };
    }
    case 'CLEAR_ALL_OVERRIDES':
      return {
        ...state,
        slotOverrides: {},
        plannedItemsBySlot: replanWithOverrides(state, {}),
      };
    case 'PREVIEW': {
      const baseline = action.baseline;
      const planned = applyOverrides(
        baseline,
        state.slotOverrides,
        effectiveSlots(state),
        state.shelfLife,
      );
      return {
        ...state,
        baseline,
        plannedItemsBySlot: planned,
        status: 'ready',
      };
    }
    case 'START_CONFIRM':
      return { ...state, confirming: true };
    case 'CANCEL_CONFIRM':
      return { ...state, confirming: false };
    case 'SUBMITTING':
      return { ...state, status: 'submitting', confirming: false };
    case 'SUBMITTED':
      return {
        ...initial,
        status: 'ready',
        layoutName: state.layoutName,
        slots: state.slots,
        shelfLife: state.shelfLife,
      };
    case 'SUBMIT_ERROR':
      return { ...state, status: 'ready', errorMessage: action.message };
    default:
      return state;
  }
}

// --- component ------------------------------------------------------------

function pickTone(slotItems) {
  if (!slotItems || slotItems.length === 0) return 'empty';
  const now = Date.now();
  const threshold = now + 2 * 86400000;
  const hasExpired = slotItems.some(
    (it) => it.status === 'EXPIRED' || it.expirationDate < now,
  );
  if (hasExpired) return 'expired';
  const hasExpiring = slotItems.some((it) => it.expirationDate < threshold);
  if (hasExpiring) return 'expiring';
  return 'fresh';
}

function formatOffset(d) {
  if (d < 0) return `${Math.abs(d)}d exp`;
  if (d === 0) return 'today';
  return `${d}d`;
}

function expirationLabel(items) {
  if (!items || items.length === 0) return null;
  const now = Date.now();
  // Bucket items by days-until-expiration so mixed-shelf-life modes show
  // exactly how many items live at each offset, e.g. "2×1d · 3d · 2×7d".
  const counts = {};
  items.forEach((it) => {
    const d = Math.round((it.expirationDate - now) / 86400000);
    counts[d] = (counts[d] || 0) + 1;
  });
  const keys = Object.keys(counts)
    .map(Number)
    .sort((a, b) => a - b);
  if (keys.length === 1) return formatOffset(keys[0]);
  return keys
    .map((d) => {
      const c = counts[d];
      const label = formatOffset(d);
      return c > 1 ? `${c}×${label}` : label;
    })
    .join(' · ');
}

export default function SeedFridgeInventory() {
  const locationId = useMemo(() => {
    const parts = window.location.pathname.split('/').filter(Boolean);
    return parts[parts.length - 1];
  }, []);

  const [state, dispatch] = useReducer(reducer, initial);
  const [prodConfirmText, setProdConfirmText] = useState('');
  // Drives a periodic re-render so Date.now()-based expiration pills
  // (pickTone / expirationLabel) don't go stale if the page sits open past
  // midnight Chicago time.
  const [, setTick] = useState(0);
  const tickRef = useRef();
  const isProd = isProdCmsBuild();
  const prodConfirmSatisfied = !isProd || prodConfirmText === locationId;

  // Load layout on mount. Hits a 1-hour localStorage cache first so a repeat
  // visit is instant; falls back to live GraphQL on miss / stale.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      const cached = readCache(locationId);
      if (cached) {
        dispatch({ type: 'LAYOUT_LOADED', ...cached });
        return;
      }
      try {
        const locations = await GetLocation(locationId);
        const loc = locations && locations[0];
        if (!loc || !loc.layoutName) {
          throw new Error(`Location ${locationId} has no layoutName`);
        }
        const layoutResp = await GetLayout({ layoutName: loc.layoutName });
        const items = (layoutResp && layoutResp.items) || [];
        const slots = items
          .filter((i) => i.slot)
          .map((i) => ({
            x: i.slot.x,
            y: i.slot.y,
            vendItemName: i.vendItemName,
            maxPerSlot: Number(i.maxPar || 0),
          }))
          .sort((a, b) => a.x - b.x || a.y - b.y);
        const shelfLife = await VendItemShelfLifeCache.getShelfLifeMap(
          locationId,
        );
        if (!cancelled) {
          dispatch({
            type: 'LAYOUT_LOADED',
            layoutName: loc.layoutName,
            slots,
            shelfLife,
          });
          writeCache(locationId, {
            layoutName: loc.layoutName,
            slots,
            shelfLife,
          });
        }
      } catch (e) {
        if (!cancelled) {
          dispatch({
            type: 'LOAD_ERROR',
            message: `Could not load layout for ${locationId}: ${
              e.message || e
            }`,
          });
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [locationId]);

  // Refresh expiration pills once a minute.
  useEffect(() => {
    tickRef.current = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(tickRef.current);
  }, []);

  // Clear the type-to-confirm field whenever the modal is not open.
  useEffect(() => {
    if (!state.confirming) setProdConfirmText('');
  }, [state.confirming]);

  // Keyboard shortcuts: Esc cancels the confirm modal, `/` focuses the mode picker.
  const modePickerRef = useRef();
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') {
        if (state.confirming) dispatch({ type: 'CANCEL_CONFIRM' });
        else if (state.editingSlot) dispatch({ type: 'CLOSE_SLOT_EDITOR' });
      } else if (
        e.key === '/' &&
        !state.confirming &&
        !state.editingSlot &&
        !['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)
      ) {
        e.preventDefault();
        if (modePickerRef.current && modePickerRef.current.focus) {
          modePickerRef.current.focus();
        }
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [state.confirming, state.editingSlot]);

  const slotsForBuilder = useMemo(
    () =>
      state.onlyOneSlotPerSku ? collapseSkuSlots(state.slots) : state.slots,
    [state.slots, state.onlyOneSlotPerSku],
  );

  const menuVendItemOptions = useMemo(
    () =>
      Object.keys(state.shelfLife || {})
        .sort()
        .map((name) => ({ value: name, label: name })),
    [state.shelfLife],
  );

  const modeOption = FILL_MODE_DESCRIPTIONS[state.mode];

  const handlePreview = useCallback(() => {
    const opts = {};
    if (state.fillCount !== '' && !Number.isNaN(Number(state.fillCount))) {
      opts.fillCount = Number(state.fillCount);
    }
    if (
      state.shelfLifeDays !== '' &&
      !Number.isNaN(Number(state.shelfLifeDays))
    ) {
      opts.shelfLifeDays = Number(state.shelfLifeDays);
    }
    const baseline = buildItemsBySlot(
      state.mode,
      slotsForBuilder,
      state.shelfLife,
      opts,
    );
    dispatch({ type: 'PREVIEW', baseline });
  }, [
    state.mode,
    state.fillCount,
    state.shelfLifeDays,
    slotsForBuilder,
    state.shelfLife,
  ]);

  const handleExportConfig = useCallback(() => {
    // Produce a FROM_CONFIG-compatible JSON. We lift the most-common values
    // (daysUntilExpiration, status, count for uniform slots) into `defaults`
    // so the exported file stays compact — per-slot entries only include
    // fields that *differ* from the default.
    const source = state.plannedItemsBySlot;
    if (!source) {
      toast.info('Click "Preview planned state" first, then Export.');
      return;
    }
    const MS_PER_DAY = 86400000;
    const now = Date.now();
    const daysUntil = (ms) => Math.round((ms - now) / MS_PER_DAY);

    // Pass 1: collapse each slot into its most compact representation.
    //   empty slot       → { empty: true }
    //   uniform slot     → { vendItemName, count, daysUntilExpiration, status }
    //   non-uniform slot → { items: [{ vendItemName, daysUntilExpiration, status }, ...] }
    const raw = {};
    Object.keys(source).forEach((fmt) => {
      const items = (source[fmt] && source[fmt].itemsInSlot) || [];
      if (items.length === 0) {
        raw[fmt] = { empty: true };
        return;
      }
      const first = items[0];
      const firstDays = daysUntil(first.expirationDate);
      const uniform = items.every(
        (it) =>
          it.vendItemName === first.vendItemName &&
          daysUntil(it.expirationDate) === firstDays &&
          it.status === first.status,
      );
      if (uniform) {
        raw[fmt] = {
          vendItemName: first.vendItemName,
          count: items.length,
          daysUntilExpiration: firstDays,
          status: first.status,
        };
      } else {
        raw[fmt] = {
          items: items.map((it) => ({
            vendItemName: it.vendItemName,
            daysUntilExpiration: daysUntil(it.expirationDate),
            status: it.status,
          })),
        };
      }
    });

    // Pass 2: tally candidates for `defaults`.
    const tallies = { daysUntilExpiration: {}, status: {}, count: {} };
    Object.values(raw).forEach((s) => {
      if (s.empty) return;
      if (s.items) {
        s.items.forEach((it) => {
          tallies.daysUntilExpiration[it.daysUntilExpiration] =
            (tallies.daysUntilExpiration[it.daysUntilExpiration] || 0) + 1;
          tallies.status[it.status] = (tallies.status[it.status] || 0) + 1;
        });
      } else {
        tallies.daysUntilExpiration[s.daysUntilExpiration] =
          (tallies.daysUntilExpiration[s.daysUntilExpiration] || 0) + 1;
        tallies.status[s.status] = (tallies.status[s.status] || 0) + 1;
        tallies.count[s.count] = (tallies.count[s.count] || 0) + 1;
      }
    });
    const mostCommon = (m) => {
      const keys = Object.keys(m);
      if (!keys.length) return undefined;
      return keys.reduce((a, b) => (m[a] >= m[b] ? a : b));
    };
    const defaults = {};
    const defDays = mostCommon(tallies.daysUntilExpiration);
    const defStatus = mostCommon(tallies.status);
    const defCount = mostCommon(tallies.count);
    if (defDays !== undefined) defaults.daysUntilExpiration = Number(defDays);
    if (defStatus !== undefined) defaults.status = defStatus;
    if (defCount !== undefined) defaults.count = Number(defCount);

    // Pass 3: strip fields that match defaults.
    const slots = {};
    Object.keys(raw).forEach((fmt) => {
      const s = raw[fmt];
      if (s.empty) {
        slots[fmt] = s;
        return;
      }
      if (s.items) {
        slots[fmt] = {
          items: s.items.map((it) => {
            const clean = { ...it };
            if (clean.daysUntilExpiration === defaults.daysUntilExpiration) {
              delete clean.daysUntilExpiration;
            }
            if (clean.status === defaults.status) delete clean.status;
            return clean;
          }),
        };
        return;
      }
      const clean = { ...s };
      if (clean.daysUntilExpiration === defaults.daysUntilExpiration) {
        delete clean.daysUntilExpiration;
      }
      if (clean.status === defaults.status) delete clean.status;
      if (clean.count === defaults.count) delete clean.count;
      slots[fmt] = clean;
    });

    const config = { onlyOneSlotPerSku: state.onlyOneSlotPerSku };
    if (Object.keys(defaults).length > 0) config.defaults = defaults;
    config.slots = slots;

    const blob = new Blob([JSON.stringify(config, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const ts = new Date().toISOString().replace(/[:.]/g, '-');
    a.href = url;
    a.download = `seed-${locationId}-${state.mode}-${ts}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(
      `Exported ${a.download} — run via CLI --fill-mode FROM_CONFIG`,
    );
  }, [
    state.plannedItemsBySlot,
    state.mode,
    state.onlyOneSlotPerSku,
    locationId,
  ]);

  const handleSubmit = useCallback(async () => {
    if (!state.plannedItemsBySlot) return;
    dispatch({ type: 'SUBMITTING' });
    try {
      const { updateId, queueMessageData } = buildQueueMessageData(
        state.plannedItemsBySlot,
      );
      await CreateQueueMessage({
        location: locationId,
        message: {
          messageId: updateId,
          messageType: 'Resource',
          payload: {
            data: JSON.stringify(queueMessageData),
            resource: 'Inventory',
          },
        },
      });
      dispatch({ type: 'SUBMITTED' });
      toast.success(
        <span>
          Inventory seeded for <strong>{locationId}</strong>.&nbsp;
          <Link to={`/locations/inventory/${locationId}`}>
            View inventory →
          </Link>
        </span>,
        { autoClose: 7000 },
      );
    } catch (e) {
      dispatch({
        type: 'SUBMIT_ERROR',
        message: `Failed to seed: ${e.message || JSON.stringify(e)}`,
      });
      toast.error(`Failed to seed inventory: ${e.message || e}`);
    }
  }, [state.plannedItemsBySlot, locationId]);

  if (!canSeedLocation(locationId)) {
    return (
      <Page>
        <Eyebrow>Seed Fridge Inventory</Eyebrow>
        <Title>Not available for this fridge</Title>
        <Subtitle>
          On the prod CMS build, seeding is restricted to FLAB_TEST_* / TEST_*
          fridges. Use a staging build or the CLI script for other fridges.
        </Subtitle>
      </Page>
    );
  }

  if (state.status === 'loading') {
    return (
      <Page>
        <Eyebrow>Seed Fridge Inventory</Eyebrow>
        <Loader />
      </Page>
    );
  }

  if (state.status === 'error' && !state.layoutName) {
    return (
      <Page>
        <Eyebrow>Seed Fridge Inventory</Eyebrow>
        <Title>Unable to load layout</Title>
        <Subtitle>{state.errorMessage}</Subtitle>
      </Page>
    );
  }

  const summary = state.plannedItemsBySlot
    ? summarizeItemsBySlot(state.plannedItemsBySlot)
    : null;

  const modeOptions = Object.keys(FILL_MODE_DESCRIPTIONS).map((key) => ({
    value: key,
    label: FILL_MODE_DESCRIPTIONS[key].label,
  }));

  const collapsedCount = state.onlyOneSlotPerSku
    ? slotsForBuilder.filter((s) => s.collapsed).length
    : 0;

  return (
    <Page>
      <HeaderBar>
        <div>
          <BackLink to={`/locations/inventory/${locationId}`}>
            ← {locationId}
          </BackLink>
          <div style={{ marginTop: theme.space.xs }}>
            <Eyebrow>Fridge Inventory · Seed</Eyebrow>
            <Title>{locationId}</Title>
            <Subtitle>
              Full-replace stocking · this will overwrite every slot in the
              fridge
            </Subtitle>
          </div>
        </div>
      </HeaderBar>

      <Layout>
        {/* Left rail: scenario controls */}
        <Card delay={60}>
          <CardLabel>Scenario</CardLabel>
          <FieldLabel>Fill Mode</FieldLabel>
          <Select
            ref={modePickerRef}
            value={modeOptions.find((o) => o.value === state.mode)}
            options={modeOptions}
            styles={reactSelectStyles}
            onChange={(o) => dispatch({ type: 'SET_MODE', mode: o.value })}
            isSearchable={false}
          />
          <ModeDescription>{modeOption.description}</ModeDescription>

          <FieldLabel>Count override (optional)</FieldLabel>
          <NumberInput
            type="number"
            min="0"
            value={state.fillCount}
            placeholder="default: maxPar"
            disabled={!modeOption.allowsFillCount}
            onChange={(e) =>
              dispatch({ type: 'SET_FILL_COUNT', value: e.target.value })
            }
          />

          <FieldLabel>Shelf life days</FieldLabel>
          <NumberInput
            type="number"
            min="0"
            value={state.shelfLifeDays}
            placeholder={modeOption.allowsShelfLife ? '14 (default)' : 'locked'}
            disabled={!modeOption.allowsShelfLife}
            onChange={(e) =>
              dispatch({ type: 'SET_SHELF_LIFE_DAYS', value: e.target.value })
            }
          />

          <ToggleRow
            onClick={(e) => {
              e.preventDefault();
              dispatch({ type: 'TOGGLE_COLLAPSE' });
            }}
          >
            <ToggleSwitch on={state.onlyOneSlotPerSku} />
            <ToggleLabel>
              Isolate vending slot per SKU
              <small>
                Many layouts assign the same SKU to multiple slots. When on,
                each SKU appears in only one slot (lowest (x,y) wins) so tests
                can assert exactly which slot the fridge vends from.
              </small>
            </ToggleLabel>
          </ToggleRow>

          <div style={{ marginTop: theme.space.lg }}>
            <GhostButton onClick={handlePreview}>
              Preview planned state
            </GhostButton>
          </div>
        </Card>

        {/* Right pane: planned state */}
        <Card delay={140}>
          <CardLabel>Planned state</CardLabel>
          {!state.plannedItemsBySlot && (
            <ModeDescription>
              Configure a scenario and click <em>Preview planned state</em> to
              see exactly what every slot will contain before seeding.
            </ModeDescription>
          )}
          {state.plannedItemsBySlot && (
            <>
              <SlotGrid>
                {(() => {
                  // Group slots by x so the grid mirrors the physical fridge —
                  // all (0,y) across the top, (1,y) second, etc. Sort rows
                  // ascending by x, sort within each row ascending by y.
                  const byRow = {};
                  slotsForBuilder.forEach((s) => {
                    if (!byRow[s.x]) byRow[s.x] = [];
                    byRow[s.x].push(s);
                  });
                  const xValues = Object.keys(byRow)
                    .map(Number)
                    .sort((a, b) => a - b);
                  let slotCounter = 0;
                  return xValues.map((x) => {
                    const rowSlots = byRow[x].slice().sort((a, b) => a.y - b.y);
                    return (
                      <React.Fragment key={`row-${x}`}>
                        <SlotRowLabel>Row {x}</SlotRowLabel>
                        <SlotRow cols={rowSlots.length}>
                          {rowSlots.map((slot) => {
                            const fmt = `(${slot.x},${slot.y})`;
                            const slotData = state.plannedItemsBySlot[fmt] || {
                              itemsInSlot: [],
                            };
                            const tone = pickTone(slotData.itemsInSlot);
                            // Label from actual items when present; otherwise
                            // "EMPTY" (or the collapsed-SKU pointer). We
                            // intentionally do NOT fall back to the layout's
                            // assigned SKU on empty slots — a slot with zero
                            // items should read "EMPTY" regardless of what the
                            // layout says it's *supposed* to hold.
                            let name = 'EMPTY';
                            const firstItem = slotData.itemsInSlot[0];
                            if (firstItem) {
                              name = firstItem.vendItemName;
                            } else if (slot.collapsed) {
                              name = `${slot.isolatedSku} lives at ${slot.vendsAt}`;
                            }
                            const label = expirationLabel(slotData.itemsInSlot);
                            const revealDelay =
                              slotCounter * theme.motion.stagger;
                            slotCounter += 1;
                            return (
                              <Slot
                                key={fmt}
                                tone={tone}
                                empty={tone === 'empty'}
                                revealDelay={revealDelay}
                                type="button"
                                onClick={() =>
                                  dispatch({
                                    type: 'OPEN_SLOT_EDITOR',
                                    slot: fmt,
                                  })
                                }
                                title="Click to override this slot"
                              >
                                <SlotCoord>
                                  <span>
                                    {fmt} · maxPar {slot.maxPerSlot}
                                  </span>
                                  {state.slotOverrides[fmt] && (
                                    <OverrideDot title="Custom override" />
                                  )}
                                </SlotCoord>
                                <SlotName>{name}</SlotName>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: theme.space.xs,
                                  }}
                                >
                                  <SlotCount>
                                    {slotData.itemsInSlot.length}
                                  </SlotCount>
                                  {label && (
                                    <SlotPill tone={tone}>{label}</SlotPill>
                                  )}
                                </div>
                              </Slot>
                            );
                          })}
                        </SlotRow>
                      </React.Fragment>
                    );
                  });
                })()}
              </SlotGrid>

              <SummaryRow>
                <span>
                  <strong>{summary.totalItems}</strong> items
                </span>
                <span>
                  <strong>{summary.nonEmptySlots}</strong>/{summary.slotCount}{' '}
                  slots non-empty
                </span>
                {summary.expiringCount > 0 && (
                  <span>
                    <strong style={{ color: theme.color.warn }}>
                      {summary.expiringCount}
                    </strong>{' '}
                    expiring
                  </span>
                )}
                {summary.expiredCount > 0 && (
                  <span>
                    <strong style={{ color: theme.color.danger }}>
                      {summary.expiredCount}
                    </strong>{' '}
                    expired
                  </span>
                )}
                {collapsedCount > 0 && (
                  <span>
                    <strong>{collapsedCount}</strong> collapsed
                  </span>
                )}
              </SummaryRow>

              <div
                style={{
                  marginTop: theme.space.xl,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: theme.space.md,
                  flexWrap: 'wrap',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: theme.space.sm,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  {Object.keys(state.slotOverrides).length > 0 && (
                    <GhostButton
                      onClick={() => dispatch({ type: 'CLEAR_ALL_OVERRIDES' })}
                    >
                      Clear {Object.keys(state.slotOverrides).length} override
                      {Object.keys(state.slotOverrides).length === 1 ? '' : 's'}
                    </GhostButton>
                  )}
                  <GhostButton onClick={handleExportConfig}>
                    Export JSON
                  </GhostButton>
                </div>
                <PrimaryButton
                  onClick={() => dispatch({ type: 'START_CONFIRM' })}
                  disabled={state.status === 'submitting'}
                >
                  Seed inventory →
                </PrimaryButton>
              </div>
            </>
          )}
        </Card>
      </Layout>

      <TelemetryStrip>
        <span>
          LOC: <strong>{locationId}</strong>
        </span>
        <span>
          LAYOUT: <strong>{state.layoutName}</strong>
        </span>
        <span>
          SLOTS: <strong>{state.slots.length}</strong>
        </span>
        <span>
          MODE: <strong>{state.mode}</strong>
        </span>
      </TelemetryStrip>

      {state.editingSlot &&
        (() => {
          const slot = slotsForBuilder.find(
            (s) => `(${s.x},${s.y})` === state.editingSlot,
          );
          if (!slot) return null;
          return (
            <SlotEditorModal
              slotFmt={state.editingSlot}
              slot={slot}
              existingOverride={state.slotOverrides[state.editingSlot]}
              shelfLife={state.shelfLife}
              plannedItemsBySlot={state.plannedItemsBySlot}
              menuVendItemOptions={menuVendItemOptions}
              onSave={(override) =>
                dispatch({
                  type: 'SET_SLOT_OVERRIDE',
                  slot: state.editingSlot,
                  override,
                })
              }
              onClear={() =>
                dispatch({
                  type: 'CLEAR_SLOT_OVERRIDE',
                  slot: state.editingSlot,
                })
              }
              onCancel={() => dispatch({ type: 'CLOSE_SLOT_EDITOR' })}
            />
          );
        })()}

      {state.confirming &&
        ReactDOM.createPortal(
          <ModalBackdrop onClick={() => dispatch({ type: 'CANCEL_CONFIRM' })}>
            <ModalCard onClick={(e) => e.stopPropagation()}>
              {isProd && (
                <ProdBanner>
                  ⚠ Production target — this write hits real prod inventory
                </ProdBanner>
              )}
              <ModalTitle>Seed {locationId}?</ModalTitle>
              <ModalBody>
                This will replace every slot on this fridge with the planned
                state: <strong>{summary.totalItems}</strong> items across{' '}
                <strong>{summary.nonEmptySlots}</strong> slots ({state.mode}).
                The fridge will pick this up on its next poll.
              </ModalBody>
              {isProd && (
                <>
                  <ProdConfirmLabel>
                    Type <code>{locationId}</code> to confirm
                  </ProdConfirmLabel>
                  <NumberInput
                    as="input"
                    type="text"
                    value={prodConfirmText}
                    onChange={(e) => setProdConfirmText(e.target.value)}
                    placeholder={locationId}
                    autoFocus
                  />
                </>
              )}
              <ModalActions>
                <GhostButton
                  onClick={() => dispatch({ type: 'CANCEL_CONFIRM' })}
                >
                  Cancel
                </GhostButton>
                <PrimaryButton
                  onClick={handleSubmit}
                  disabled={
                    state.status === 'submitting' || !prodConfirmSatisfied
                  }
                >
                  {state.status === 'submitting' ? 'Seeding…' : 'Yes, seed'}
                </PrimaryButton>
              </ModalActions>
            </ModalCard>
          </ModalBackdrop>,
          document.body,
        )}
    </Page>
  );
}
